import { adjustedCategory, categoryLabel } from "../config/productCategories";
import React, { useState, useMemo, useRef, useEffect, useCallback, memo } from "react";
import { useQuery } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { MapPin, X, Copy, Plus, Info, Trash2, Barcode, Loader2, Share2, EyeOff } from "lucide-react";
import type { ProductListItem } from "../types/Product";
import type {
  CategoryV2,
  HomeDepotCategory,
} from "../services/CategoriesService";
import type { StoreProductResult } from "../services/ProductSearchService";
import { type HomeDepotStoreProductResult } from "../services/ProductSearchService";
import BarcodeComponent from "./Barcode";
import { toast } from "react-toastify";
import { getAffiliateLink, getProductStoreMatches, getPublicAffiliateLink } from "../services/ProductsService";
import { getRetailerLinkLabel } from "../utils/productUrls";
import { createDeal, getDealShareUrl } from "../services/DealsService";
import { useTranslation } from "../context/LanguageContext";
import { useUserContext } from "../context/UserContext";
import { getProductImageUrl } from "../utils/imageUtils";
import SaveButton from "./buttons/SaveButton";
import SetAlertButton from "./buttons/SetAlertButton";
import ProductDetailModal from "./ProductDetailModal";
import { getProductDetailIdentifier } from "./ProductDetailModal.utils";
import {
  areProductItemPropsEqual,
  formatStoreId,
  formatFirstAppeared,
  formatLastUpdated,
  formatPrice,
  getAllProductsWithSameUpc,
  getCategoryName,
  getDiscountPercent,
  getDisplayedLastChangeTimestamp,
  getCashbackAmount,
  getIbottaOffers,
  getCashbackMinQty,
  getOfferType,
  getOfferLabel,
  getMultiBuyPriceDisplay,
  getIsNewProduct,
  getLastChangeTimestamp,
  getLastSeenTimestamp,
  getLastUpdatedTimestamp,
  getLatestValue,
  getProductUrl,
  getSavingsAmount,
  getStockChange,
  getStoreCoordinates,
  getPriceChange,
  getWalmartBarcodeFromProduct,
  isScannableBarcode,
  isValidUpc,
  readCachedProductBarcode,
  resolveWalmartSkuUpc,
  writeCachedProductBarcode,
} from "./ProductItem.utils";
import IbottaOfferChip from "./IbottaOfferChip";
import { ProductOfferPrice } from "./ProductOfferPrice";
import { getSearchParams } from "./buttons/CheckStockButton.utils";
import { getRetailerDisplayName, isOnlineOnlyRetailer, type RetailerType } from "../config/retailers";
import ShareDealSheet from "./ShareDealSheet";
import type { StoreData } from "../hooks/useStores";

import StoreMapModal from "./StoreMapModal";
import { samsClubStockLabel } from "../utils/stockCount";
import { getOpenBoxBadgeText, getRetailerLookupSku } from "../utils/openBox";

interface StoreLookupRecord {
  store_id?: string;
  storeNumber?: string;
  store_number?: string;
  "Store No."?: number;
  warehouse_number?: number;
  label?: string;
  city?: string;
  City?: string;
  warehouse_name?: string;
  address?: string;
  location?: string;
}

interface ProductItemProps {
  index: number;
  style: React.CSSProperties;
  data: {
    products: ProductListItem[];
    productsByUPC?: Map<string, ProductListItem[]>; // Pre-computed map for efficient lookup
    storeCounts?: Record<string, number>;
    showHotDeals?: boolean;
    showPennyItems?: boolean;
    categories: CategoryV2[];
    homeDepotCategories?: HomeDepotCategory[];
    onToggleExpanded?: (productId: string) => void;
    onProductSearch?: (
      code: string,
      type: "UPC" | "SKU",
      productId?: string,
      purpose?: "nearby" | "barcode",
      productName?: string
    ) => void;
    onSearchReset?: (productId: string) => void;
    searchResults?: StoreProductResult[];
    homeDepotSearchResults?: HomeDepotStoreProductResult[];
    searchAmazonData?: { price?: number; title?: string; url?: string; isPrime?: boolean } | null;
    searchError?: string | null;
    searchProductId?: string | null;
    isSearching?: boolean;
    retailer?: string;
    selectedStoreId?: string;
    storeFilterOptions?: Array<{
      id: string;
      name: string;
      isAll?: boolean;
    }>;
    loadedStores?: StoreData;
    isSavedMode?: boolean;
    onRemoveSaved?: (upc: string) => void;
    onHideProduct?: (product: ProductListItem) => void;
    disableFavorites?: boolean;
    sortBy?: string;
  };
}

interface WalmartBarcodeActionsProps {
  barcode?: string;
  onBarcodeClick: (e: React.MouseEvent, barcode?: string) => void;
  onCopyToClipboard: (e: React.SyntheticEvent, value: string, label: string) => void;
}

interface ProductDetailTriggerProps {
  children: React.ReactNode;
  sku: string;
  storeId: string;
  retailer: string;
  className?: string;
  style?: React.CSSProperties;
  scrollToChart?: boolean;
}

const EXPANDED_PANEL_DURATION_MS = 260;

const WalmartBarcodeActions: React.FC<WalmartBarcodeActionsProps> = ({
  barcode,
  onBarcodeClick,
  onCopyToClipboard,
}) => {
  const { t } = useTranslation();
  return (
  <div className="flex items-center gap-1">
    {barcode ? (
      <>
        <span>{t("card.upc")}: </span>
        <button
          onClick={(e) => onBarcodeClick(e, barcode)}
          className="text-purple-600 dark:text-purple-400 underline font-medium hover:text-purple-700 dark:hover:text-purple-300 cursor-pointer"
        >
          {barcode}
        </button>
        <button
          onPointerDown={(e) => onCopyToClipboard(e, barcode, t("card.upc"))}
          className="p-0.5 text-gray-400 dark:text-dark-500 hover:text-gray-600 dark:hover:text-dark-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded transition-colors cursor-pointer"
          title={t("card.copyCode", { code: t("card.upc") })}
          style={{ touchAction: "manipulation" }}
        >
          <Copy className="w-3 h-3" />
        </button>
      </>
    ) : (
      <button
        onClick={(e) => onBarcodeClick(e)}
        className="text-purple-600 dark:text-purple-400 underline font-medium hover:text-purple-700 dark:hover:text-purple-300 cursor-pointer"
      >
        {t("card.viewBarcode")}
      </button>
    )}
  </div>
  );
};

const ProductDetailTrigger = memo(function ProductDetailTrigger({
  children,
  sku,
  storeId,
  retailer,
  className,
  style,
  scrollToChart = false,
}: ProductDetailTriggerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const handleOpen = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      clearTimers();
      setIsOpen(true);
      openTimerRef.current = window.setTimeout(() => {
        setIsAnimating(true);
        openTimerRef.current = null;
      }, 10);
    },
    [clearTimers]
  );

  const handleClose = useCallback(() => {
    clearTimers();
    setIsAnimating(false);
    closeTimerRef.current = window.setTimeout(() => {
      setIsOpen(false);
      closeTimerRef.current = null;
    }, 300);
  }, [clearTimers]);

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={className}
        style={style}
      >
        {children}
      </button>

      {isOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-end justify-center"
            style={{
              backgroundColor: isAnimating ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0)",
              transition: "background-color 250ms ease-out",
            }}
            onPointerDown={(e) => {
              if (e.target === e.currentTarget) {
                handleClose();
              }
            }}
          >
            <div
              className={`bg-white dark:bg-dark-900 w-full h-[75vh] rounded-t-xl overflow-hidden flex flex-col transform transition-all duration-300 ease-out ${
                isAnimating ? "translate-y-0" : "translate-y-full"
              }`}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <ProductDetailModal
                sku={sku}
                storeId={storeId}
                retailer={retailer}
                onClose={handleClose}
                scrollToChart={scrollToChart}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
});

const ProductItem: React.FC<ProductItemProps> = ({ index, style, data }) => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const navigate = useNavigate();
  const itemRef = useRef<HTMLDivElement>(null);
  const {
    products,
    productsByUPC,
    storeCounts,
    showHotDeals = false,
    showPennyItems = false,
    categories,
    homeDepotCategories,
    onToggleExpanded,
    onProductSearch,
    onSearchReset,
    searchResults,
    searchError,
    searchProductId,
    isSearching,
    retailer = "walmart",
    selectedStoreId = "all",
    storeFilterOptions,
    loadedStores,
    isSavedMode,
    onRemoveSaved,
    onHideProduct,
    disableFavorites,
  } = data;
  const product = products[index];
  const variantTitleSuffix = user?.admin === true && product.isVariant ? " *" : "";

  // Reset image loading state when product changes
  useEffect(() => {
    if (product.image) {
      setImageLoading(true);
      // Check if image is already loaded (cached)
      if (imageRef.current?.complete) {
        // Small delay to ensure skeleton is visible
        setTimeout(() => setImageLoading(false), 50);
      }
    }
  }, [product.image]);

  // Find all products with the same UPC across all stores - use pre-computed map if available
  const initiallyLoadedProductsWithSameUPC = useMemo(() => {
    if (productsByUPC) {
      const matches = productsByUPC.get(product.upc || product.sku) || [];
      return matches.length ? matches : [product];
    }
    const matches = getAllProductsWithSameUpc(products, productsByUPC, product.upc);
    return matches.length ? matches : [product];
  }, [productsByUPC, products, product]);

  // State for multi-store modal
  const [showMultiStoreModal, setShowMultiStoreModal] = useState(false);

  // State for Home Depot zip code input
  const [showHomeDepotZipInput, setShowHomeDepotZipInput] = useState(false);
  const [homeDepotZipCode, setHomeDepotZipCode] = useState("");

  // State for map modal
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedStoreForMap, setSelectedStoreForMap] = useState<{
    address: string;
    name: string;
    coordinates?: [number, number];
  } | null>(null);

  // State for image loading
  const [imageLoading, setImageLoading] = useState(true);
  const imageRef = useRef<HTMLImageElement>(null);

  // State for Target clearance info popup
  const [showClearanceInfo, setShowClearanceInfo] = useState(false);

  // State for barcode sheet
  const [showBarcodeSheet, setShowBarcodeSheet] = useState(false);
  const [isBarcodeSheetAnimating, setIsBarcodeSheetAnimating] = useState(false);
  const [isFetchingBarcode, setIsFetchingBarcode] = useState(false);
  const [fetchedBarcode, setFetchedBarcode] = useState<string | null>(() =>
    readCachedProductBarcode(product.upc, product.storeId) ?? null
  );
  const [isBarcodeOnlyFetch, setIsBarcodeOnlyFetch] = useState(false);

  useEffect(() => {
    setFetchedBarcode(readCachedProductBarcode(product.upc, product.storeId) ?? null);
  }, [product.upc, product.storeId]);

  // Helper function to get store coordinates by store ID

  // Handle map modal open
  const handleOpenMapModal = (
    storeAddress: string,
    storeName: string,
    storeId?: string
  ) => {
    const coordinates = storeId
      ? getStoreCoordinates({ storeId, retailer, loadedStores })
      : undefined;
    setSelectedStoreForMap({
      address: storeAddress,
      name: storeName,
      coordinates,
    });
    setShowMapModal(true);
  };

  // Check retailer types
  const isHomeDepot = retailer === "home_depot";
  const isTarget = retailer === "target";
  const isDollarGeneral = retailer === "dollar_general";
  const isBestBuy = retailer === "bestbuy";
  const isCostco = retailer === "costco";
  const isLowes = retailer === "lowes";
  // Sam's Club has no nearby-stock endpoint.
  const isTractorSupply = retailer === "tractor_supply";
  const isLocalClearance = isTractorSupply || retailer === "northern_tool" || retailer === "samsclub";
  const isOnlineRetailer = isOnlineOnlyRetailer(retailer);
  const isWoot = retailer === "woot";
  const isAmazon = retailer === "amazon";
  const isOnlineStoreId = product.storeId === "online";
  const productDetailIdentifier = getProductDetailIdentifier(product, retailer);
  const isHomeDepotOnline = isHomeDepot && product.storeId === "online";
  const isBestBuyOpenBox = isBestBuy && Boolean(product.openBox);
  const priceHistory = isBestBuyOpenBox && product.openBoxPrice?.length
    ? product.storePrice
    : product.storePrice;

  // Penny products live in a pseudo-store bucket so they never mix with the
  // retailer's normal product feed. Inventory remains a possible-stock signal
  // until the shopper verifies the item in store.
  const isPennyItem = (isHomeDepot || isDollarGeneral) && product.storeId.includes("-PENNY-ITEM");
  const isPotentialPennyItem = isDollarGeneral && isPennyItem;

  // Physical retailers that track an in-store aisle location.
  const canShowAisle =
    !isDollarGeneral &&
    !isOnlineRetailer &&
    !isWoot &&
    !isCostco &&
    !isLowes &&
    !isOnlineStoreId;
  const aisleLabel = canShowAisle && product.aisle ? product.aisle : null;

  const storeLabel = useMemo(() => {
    if (isOnlineRetailer || isWoot || isOnlineStoreId) return undefined;
    const id = formatStoreId(product.storeId);
    if (!id) return undefined;
    if (loadedStores && Array.isArray(loadedStores)) {
      const store = (loadedStores as StoreLookupRecord[]).find((s) =>
        s.store_id === id || s.storeNumber === id || s.store_number === id ||
        (s.store_number && s.store_number.replace(/^0+/, "") === id) ||
        String(s["Store No."]) === id || `${s.warehouse_number}-wh` === id
      );
      if (store?.label) return store.label as string;
      const city = store?.city || store?.City || store?.warehouse_name ||
        (store?.address || "").split(",")[1]?.trim() ||
        (store?.location || "").split(",")[0]?.trim();
      if (city) return `${city} - #${id}`;
    }
    const opt = storeFilterOptions?.find((o) => o.id === id);
    if (opt?.name) {
      const parts = opt.name.split(",");
      const city = parts.length >= 2 ? parts[1].trim() : opt.name.split(" ").filter((w: string) => !w.startsWith("#") && !/^Store$/i.test(w)).slice(0, 3).join(" ");
      if (city) return `${city} - #${id}`;
    }
    return `#${id}`;
  }, [product.storeId, loadedStores, storeFilterOptions, isOnlineRetailer, isWoot, isOnlineStoreId]);

  const productUrl = getProductUrl({
    url: product.url,
    retailer,
    storeId: product.storeId,
  });

  const categoryName = getCategoryName({
    categoryId: adjustedCategory(product),
    retailer,
    categories,
    homeDepotCategories,
  });

  // State management
  const [showImageModal, setShowImageModal] = useState(false);
  const [showVariantsModal, setShowVariantsModal] = useState(false);
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareSheetOpen, setShareSheetOpen] = useState(false);

  // Keep row expansion local so opening a product item does not re-render ProductsPage.
  const [isExpanded, setIsExpanded] = useState(false);
  const [showExpandedContent, setShowExpandedContent] = useState(isExpanded);
  const productKey = product.upc || product.sku || product.productKey || "";
  const storeMatchCount = storeCounts?.[productKey] ?? initiallyLoadedProductsWithSameUPC.length;
  const storeMatchesQuery = useQuery({
    queryKey: ["product-store-matches", retailer, productKey, showHotDeals, showPennyItems],
    queryFn: () => getProductStoreMatches({ retailer, productId: productKey, showHotDeals, showPennyItems, showCoupons: product.storeId?.endsWith("-COUPON") }),
    enabled:
      isExpanded &&
      selectedStoreId === "all" &&
      !isOnlineRetailer &&
      storeMatchCount > initiallyLoadedProductsWithSameUPC.length,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
  });
  const allProductsWithSameUPC = useMemo(
    () =>
      storeMatchesQuery.data?.products?.length
        ? storeMatchesQuery.data.products
        : initiallyLoadedProductsWithSameUPC,
    [initiallyLoadedProductsWithSameUPC, storeMatchesQuery.data]
  );

  useEffect(() => {
    if (isExpanded) {
      setShowExpandedContent(true);
      return;
    }

    const collapseTimeout = window.setTimeout(() => {
      setShowExpandedContent(false);
    }, EXPANDED_PANEL_DURATION_MS);

    return () => window.clearTimeout(collapseTimeout);
  }, [isExpanded]);

  // Status checking is now handled by useProductStatus hook - no need for manual useEffect


  // Check if product was inserted in the last 24 hours
  const isNewProduct = useMemo(() => {
    return getIsNewProduct(product.firstInserted);
  }, [product.firstInserted]);

  // Get the latest values from arrays
  const latestStorePrice = getLatestValue(priceHistory);
  const latestOpenBoxPrice = getLatestValue(product.openBoxPrice);
  const latestSalesFloor = getLatestValue(product.salesFloor);
  const hasInventoryCount = Array.isArray(product.salesFloor) && product.salesFloor.length > 0;

  // Check for price change (increase or decrease)
  const priceChange = useMemo(() => {
    return getPriceChange(priceHistory);
  }, [priceHistory]);

  const hasPriceChange = priceChange !== null;

  // Check for inventory change (increase or decrease)
  const stockChange = useMemo(() => {
    return getStockChange(product.salesFloor);
  }, [product.salesFloor]);

  const hasStockChange = stockChange !== null;

  const isAtLowest = useMemo(() => {
    const prices = product.storePrice;
    if (!prices || prices.length < 3) return false;
    const current = prices[prices.length - 1];
    if (current <= 0) return false;
    const recentWindow = prices.slice(Math.max(0, prices.length - 4), prices.length - 1);
    if (!recentWindow.every((p) => p > current)) return false;
    for (let i = 0; i < prices.length - 1; i++) {
      if (prices[i] > 0 && prices[i] < current) return false;
    }
    return true;
  }, [product.storePrice]);

  // Calculate discount percentage and savings amount (compared to online price)
  const msrp = product.msrp || 0;
  const cashbackAmount = getCashbackAmount(product);
  const cashbackMinQty = getCashbackMinQty(product);
  const ibottaOffers = getIbottaOffers(product);
  const cashbackLabel = cashbackMinQty > 1
    ? t("products.cashbackAmountMinQty", { amount: formatPrice(cashbackAmount), qty: String(cashbackMinQty) })
    : t("products.cashbackAmount", { amount: formatPrice(cashbackAmount) });
  const offerType = getOfferType(product);
  const offerLabel = getOfferLabel(product, {
    bogo: t("products.offerBogo"),
    mixMatch: t("products.offerMixMatch"),
  });
  const multiBuy = getMultiBuyPriceDisplay(product, latestStorePrice, msrp);
  const discountPercent = getDiscountPercent(product.msrp, latestStorePrice);
  const savingsAmount = getSavingsAmount(msrp, latestStorePrice);
  const lastChangeTimestamp = useMemo(() => {
    return getLastChangeTimestamp({
      priceHistory,
      backroomHistory: product.backroom,
      salesFloorHistory: product.salesFloor,
      lastUpdates: product.lastUpdates,
    });
  }, [priceHistory, product.backroom, product.salesFloor, product.lastUpdates]);
  const lastUpdatedTimestamp = useMemo(
    () =>
      getLastUpdatedTimestamp({
        priceHistory,
        backroomHistory: product.backroom,
        salesFloorHistory: product.salesFloor,
        lastUpdates: product.lastUpdates,
        firstInserted: product.firstInserted,
        lastUpdated: product.lastUpdated,
      }),
    [
      priceHistory,
      product.backroom,
      product.salesFloor,
      product.lastUpdates,
      product.firstInserted,
      product.lastUpdated,
    ]
  );
  const lastSeenTimestamp = getLastSeenTimestamp(product.lastUpdates, product.firstInserted);
  const displayedLastChangeTimestamp = getDisplayedLastChangeTimestamp(
    lastChangeTimestamp,
    lastUpdatedTimestamp
  );
  const lastChangeLabel = formatLastUpdated(
    displayedLastChangeTimestamp ? [displayedLastChangeTimestamp] : [],
    { emptyLabel: t("products.noUpdates"), t }
  );
  const lastUpdatedLabel = formatLastUpdated([lastUpdatedTimestamp], {
    emptyLabel: t("products.noUpdates"),
    t,
  });
  const lastSeenLabel = formatLastUpdated([lastSeenTimestamp], {
    emptyLabel: t("products.noUpdates"),
    t,
  });
  const pennyFirstSpottedLabel = formatFirstAppeared(product.firstInserted, { t });
  const pennyLastRegularLabel = lastSeenLabel;
  const variantCriteria = useMemo(
    () =>
      (product.variantCriteria || []).filter(
        (option) =>
          option?.sku &&
          option.sku !== product.sku &&
          option.sku !== product.upc
      ),
    [product.variantCriteria, product.sku, product.upc]
  );
  const hasVariantCriteria = variantCriteria.length > 0;

  // Handle product click to toggle expanded state
  const handleProductClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const productId = product.upc || product.sku || "";
    if (productId) {
      onToggleExpanded?.(productId);
      setIsExpanded((prev) => !prev);
    }
  };

  const handleVariantCriteriaClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const productId = product.upc || product.sku || "";
    if (productId && !isExpanded) {
      onToggleExpanded?.(productId);
    }
    setIsExpanded(true);
  };

  const handleVariantOptionClick = (
    e: React.MouseEvent,
    option: { sku: string; displayName?: string; name?: string }
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (!option.sku) return;

    const optionLabel = option.displayName || option.name || product.name;

    if (onProductSearch) {
      onProductSearch(option.sku, "SKU", option.sku, "nearby", optionLabel);
    } else {
      navigate(`/product-search?sku=${encodeURIComponent(option.sku)}&auto=true`);
    }
  };

  // Utility function to stop event propagation without breaking functionality
  const stopEventPropagation = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
  };

  // Utility functions for zip code storage
  const getStoredZipCode = (): string => {
    try {
      const stored = localStorage.getItem("dealhawk-zip-code");
      return stored || "";
    } catch (error) {
      console.error("Error reading stored zip code:", error);
      return "";
    }
  };

  const saveZipCode = (newZipCode: string) => {
    try {
      localStorage.setItem("dealhawk-zip-code", newZipCode);
    } catch (error) {
      console.error("Error saving zip code:", error);
    }
  };

  const isWalmart = retailer === "walmart" || retailer === "walmart_online";
  const walmartCodes = useMemo(
    () => (isWalmart ? resolveWalmartSkuUpc(product.upc, product.sku) : null),
    [isWalmart, product.upc, product.sku]
  );
  const hasAffiliateLink = Boolean(product.productKey) || isWalmart || isTarget || retailer === "target_online" || isAmazon;

  const handleAffiliateClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (affiliateLoading) return;
    const tab = window.open("about:blank", "_blank");
    setAffiliateLoading(true);
    try {
      const trackingUrl = product.productKey
        ? await getPublicAffiliateLink(product.productKey)
        : await getAffiliateLink(product.upc || product.sku, productUrl, retailer);
      if (tab) tab.location.href = trackingUrl;
      else window.location.href = trackingUrl;
    } catch {
      if (product.productKey) {
        if (tab) tab.close();
      } else if (tab) tab.location.href = productUrl;
      else window.location.href = productUrl;
    } finally {
      setAffiliateLoading(false);
    }
  };

  // Card-level share: the same affiliate link the retailer button opens, so
  // every shared deal can earn a commission.
  const resolveCardShareUrl = async () => {
    try {
      const link = product.productKey
        ? await getPublicAffiliateLink(product.productKey)
        : await getAffiliateLink(product.upc || product.sku, productUrl, retailer);
      if (link) return link;
    } catch {
      // fall back to the plain retailer page below
    }
    if (!productUrl) throw new Error("No link");
    return productUrl;
  };
  const openCardShareSheet = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShareSheetOpen(true);
  };

  const handleShareDeal = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (shareLoading) return;
    setShareLoading(true);
    try {
      const dealId = await createDeal({
        upc: product.upc, sku: product.sku, name: product.name,
        msrp: product.msrp, onlinePrice: product.onlinePrice,
        storePrice: latestStorePrice, image: product.image,
        storeId: product.storeId, retailer,
        category: product.category, url: product.url || "",
        aisle: product.aisle, inStock: latestSalesFloor > 0,
        discountPercentage: product.discountPercentage,
      });
      const shareUrl = getDealShareUrl(dealId);
      if (navigator.share) {
        try {
          await navigator.share({
            title: product.name,
            text: `Check out this deal: ${product.name}`,
            url: shareUrl,
          });
          return;
        } catch (shareError) {
          if (shareError instanceof DOMException && shareError.name === "AbortError") return;
        }
      }
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("card.shareLinkCopied"));
    } catch {
      toast.error(t("card.shareFailed"));
    } finally {
      setShareLoading(false);
    }
  };

  // Handle view stores
  const handleViewStores = (e: React.MouseEvent | React.TouchEvent) => {
    stopEventPropagation(e);

    if (!product?.upc && !product?.sku) return;

    if (isHomeDepot) {
      // For Home Depot, we need a zip code and itemId
      const storedZip = getStoredZipCode();
      if (storedZip) {
        setHomeDepotZipCode(storedZip);
        handleHomeDepotSearch();
      } else {
        setShowHomeDepotZipInput(true);
      }
    } else {
      // For Walmart and other retailers, always use the callback
      const { searchCode, searchType } = getSearchParams({
        retailer,
        productSku: getRetailerLookupSku(product),
        productUpc: product.upc || undefined,
      });
      const productId = product.upc || product.sku || "";

      // Use the onProductSearch callback if available, otherwise navigate (fallback)
      if (onProductSearch) {
        onProductSearch(searchCode, searchType, productId, "nearby", product.name);
      } else if (!isLowes) {
        // Fallback to navigation for backward compatibility
        navigate(
          `/product-search?${searchType === "UPC" ? "upc" : "sku"}=${encodeURIComponent(searchCode)}&auto=true`
        );
      }
    }
  };

  // Handle Home Depot search
  const handleHomeDepotSearch = async () => {
    const itemId = product.upc || product.sku; // Use itemId field for Home Depot, fallback to sku
    if (!itemId) return;

    try {
      if (onProductSearch) {
        // Use the callback to trigger search in parent component
        onProductSearch(itemId, "SKU", itemId, "nearby", product.name);
      }
    } catch (error) {
      console.error("Error searching Home Depot stores:", error);
      toast.error(t("card.hdSearchFailed"));
    }
  };

  // Handle Home Depot zip code submission
  const handleHomeDepotZipSubmit = async () => {
    if (!homeDepotZipCode.trim()) {
      toast.error(t("card.invalidZip"));
      return;
    }

    saveZipCode(homeDepotZipCode.trim());
    setShowHomeDepotZipInput(false);
    await handleHomeDepotSearch();
  };

  // Handle copy to clipboard
  const handleCopyToClipboard = async (
    e: React.SyntheticEvent,
    value: string,
    label: string
  ) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("card.copiedToClipboard", { label }));
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = value;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success(t("card.copiedToClipboard", { label }));
    }
  };

  // Handle image click to open modal
  const handleImageClick = (e: React.MouseEvent) => {
    stopEventPropagation(e);

    if (product.image) {
      setShowImageModal(true);
    }
  };

  // Handle barcode sheet open
  const handleOpenBarcodeSheet = () => {
    setShowBarcodeSheet(true);
    setTimeout(() => setIsBarcodeSheetAnimating(true), 10);
  };

  // Handle barcode sheet close
  const handleCloseBarcodeSheet = () => {
    setIsBarcodeSheetAnimating(false);
    // Reset search if we triggered it for barcode only
    if (isBarcodeOnlyFetch && searchProductId) {
      onSearchReset?.(searchProductId);
    }
    setTimeout(() => {
      setShowBarcodeSheet(false);
      setIsFetchingBarcode(false);
      setIsBarcodeOnlyFetch(false);
    }, 300);
  };

  const getBarcodeFromProduct = (): string | undefined => {
    const fetched = fetchedBarcode?.trim();
    if (fetched) return fetched;

    if (isWalmart || retailer === "walmart_online") {
      return getWalmartBarcodeFromProduct(product.upc, product.sku);
    }

    const stored = product.sku?.trim() || product.upc?.trim();
    return stored && isScannableBarcode(stored) ? stored : undefined;
  };

  // Handle barcode action click - shows barcode directly if provided, otherwise fetches from API
  const handleBarcodeClick = (e: React.MouseEvent | React.TouchEvent, barcode?: string) => {
    stopEventPropagation(e);

    // If we already have a barcode, show it directly
    if (barcode) {
      setFetchedBarcode(barcode);
      handleOpenBarcodeSheet();
      return;
    }

    if (!product?.upc && !product?.sku) return;

    setIsFetchingBarcode(true);
    setIsBarcodeOnlyFetch(true); // Prevent nearby modal from showing
    handleOpenBarcodeSheet();

    // Trigger the search to get the barcode (use short item id for Walmart lookup)
    const searchCode = product.upc || product.sku;
    const searchType = "SKU";
    const productId = product.upc || product.sku || "";

    if (onProductSearch) {
      onProductSearch(searchCode, searchType, productId, "barcode");
    }
  };

  // Effect to capture barcode from search results
  useEffect(() => {
    if (!isFetchingBarcode) return;

    // Check if we have results
    if (searchResults && searchResults.length > 0) {
      const firstResult = searchResults.find((r) => !r.error && r.barcode);
      if (firstResult?.barcode) {
        setFetchedBarcode(firstResult.barcode);
        writeCachedProductBarcode(product.upc, product.storeId, firstResult.barcode);
        setIsFetchingBarcode(false);
        return;
      }
    }

    // If search completed without results or with error
    if (!isSearching && (searchError || (searchResults && searchResults.length === 0))) {
      setIsFetchingBarcode(false);
    }
  }, [searchResults, isFetchingBarcode, isSearching, searchError, product.storeId, product.upc]);

  // Handle view options click
  const handleViewOptionsClick = (e: React.MouseEvent) => {
    stopEventPropagation(e);
    setShowVariantsModal(true);
  };

  // Variants modal
  const variantsModal = showVariantsModal && product.variants && (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(4px)",
      }}
      onPointerDown={() => setShowVariantsModal(false)}
    >
      <div
        className="bg-white dark:bg-dark-800 rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-dark-700">
          <h3 className="text-lg font-semibold dark:text-white">{t("products.productOptions")}</h3>
          <button
            onPointerDown={() => setShowVariantsModal(false)}
            className="p-1 text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-200 rounded-full hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors cursor-pointer"
            style={{ touchAction: "manipulation" }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-auto max-h-[calc(80vh-5rem)]">
          {product.variants.map((variant, index) => (
            <div
              key={variant.upc || index}
              className="p-3 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors border-b border-gray-50 dark:border-dark-700 last:border-b-0"
            >
              <div className="flex items-start space-x-3">
                {/* Variant Image */}
                <div className="w-14 h-14 bg-gray-100 dark:bg-dark-700 rounded-lg flex-shrink-0 overflow-hidden">
                  {variant.imageUrl ? (
                    <img
                      src={getProductImageUrl(variant.imageUrl)}
                      alt={variant.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-dark-400">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Variant Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-1 line-clamp-2">
                    {variant.name}
                  </h4>
                  {isValidUpc(variant.upc) && (
                    <p className="text-xs text-gray-500 dark:text-dark-300 mb-2">{t("card.upc")}: {variant.upc}</p>
                  )}

                  {/* Variant Prices */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-base font-bold text-emerald-600">
                        {formatPrice(variant.storePrice)}
                      </span>
                      {variant.msrp > variant.storePrice && (
                        <div className="flex items-center space-x-1">
                          <div className="text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">
                            <span className="text-xs font-bold">
                              💰 {t("products.saveAmount", { amount: formatPrice(variant.msrp - variant.storePrice) })}
                            </span>
                          </div>
                          <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                            {t("products.percentOffLabel", {
                              percent: Math.round(
                                ((variant.msrp - variant.storePrice) /
                                  variant.msrp) *
                                  100
                              ),
                            })}
                          </span>
                        </div>
                      )}
                    </div>

                    {variant.msrp > 0 &&
                      variant.msrp !== variant.storePrice && (
                        <div className="text-xs text-gray-900 dark:text-white">
                          {t("card.msrp")} {formatPrice(variant.msrp)}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Image modal
  const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
  const imageModal = showImageModal && product.image && (
    <div
      className="fixed inset-0 flex items-center justify-center z-[10001] p-4"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.9)",
      }}
      onClick={(e) => {
        e.stopPropagation();
        setShowImageModal(false);
      }}
      onPointerDown={stopAll}
      onPointerUp={stopAll}
      onMouseDown={stopAll}
      onMouseUp={stopAll}
      onTouchStart={stopAll}
      onTouchEnd={stopAll}
    >
      {/* Close button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowImageModal(false);
        }}
        onPointerDown={stopAll}
        onPointerUp={stopAll}
        onMouseDown={stopAll}
        onMouseUp={stopAll}
        onTouchStart={stopAll}
        onTouchEnd={stopAll}
        className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors cursor-pointer backdrop-blur-sm"
        style={{ touchAction: "manipulation" }}
        aria-label={t("common.close")}
      >
        <X className="w-6 h-6" />
      </button>

      <div
        className="flex flex-col items-center gap-3 max-w-3xl w-full"
        onClick={stopAll}
        onPointerDown={stopAll}
        onPointerUp={stopAll}
        onMouseDown={stopAll}
        onMouseUp={stopAll}
        onTouchStart={stopAll}
        onTouchEnd={stopAll}
      >
        {/* Image */}
        <img
          src={getProductImageUrl(product.image)}
          alt={product.name}
          className="w-auto max-w-full max-h-[72vh] object-contain rounded-xl shadow-2xl bg-white"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />

        {/* Product name - directly under image */}
        <div className="bg-white/10 backdrop-blur-sm text-white px-4 py-2.5 rounded-lg w-full max-w-full text-center">
          <h3 className="font-semibold text-sm sm:text-base leading-tight line-clamp-3">
            {product.name}
          </h3>
          {isValidUpc(product.upc) && !isWoot && (
            <p className="text-[11px] sm:text-xs opacity-70 mt-1">
              {isHomeDepot ? t("card.itemId") : t("card.sku")}: {product.upc}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div
        ref={itemRef}
        style={style}
        className="bg-white dark:bg-dark-900 border-b border-gray-100 dark:border-dark-800 overflow-hidden transition-colors hover:bg-gray-50/70 dark:hover:bg-dark-800/40"
      >
        {/* Main Product Content */}
        <div className="px-3 py-[9px]">
          <div className="flex items-stretch gap-2.5">
            {/* Left Column: Image */}
            <div className="w-[72px] flex-shrink-0 flex flex-col items-center gap-[3px]">
              <div className="w-full flex flex-col items-center gap-[3px]">
                <div className="w-[70px] h-[70px] flex-shrink-0 bg-gray-50 dark:bg-dark-800 rounded-xl ring-1 ring-black/[0.04] dark:ring-white/[0.06] overflow-hidden relative">
                  {product.image ? (
                    <>
                      {imageLoading && (
                        <div className="absolute inset-0 z-10 bg-gray-200 dark:bg-dark-700 animate-pulse rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-gray-400 dark:text-dark-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                      <img
                        ref={imageRef}
                        src={getProductImageUrl(product.image, "150")}
                        alt={product.name}
                        className={`w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity ${
                          imageLoading ? "opacity-0 absolute inset-0" : "opacity-100 relative"
                        }`}
                        onClick={handleImageClick}
                        onLoad={() => setTimeout(() => setImageLoading(false), 50)}
                        onError={(e) => { e.currentTarget.style.display = "none"; setImageLoading(false); }}
                      />
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-dark-500">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                  )}
                </div>
                {/* Status Badge */}
                {(isNewProduct || hasPriceChange || hasStockChange || isAtLowest) && (
                  <div className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold text-white uppercase tracking-[0.3px] ${
                    isNewProduct ? "bg-emerald-500" :
                    isAtLowest ? "bg-violet-500" :
                    priceChange === "down" ? "bg-green-500" :
                    priceChange === "up" ? "bg-red-500" :
                    stockChange === "up" ? "bg-blue-500" : "bg-orange-500"
                  }`}>
                    {isNewProduct ? t("products.badgeNew") : isAtLowest ? t("card.lowest") : priceChange === "down" ? t("products.badgePriceDown") : priceChange === "up" ? t("products.badgePriceUp") : stockChange === "up" ? t("products.badgeStockUp") : t("products.badgeStockDown")}
                  </div>
                )}
              </div>
              <div className="w-full flex items-center justify-center gap-1 text-[9px] leading-[11px]">
                {isPennyItem ? (
                  <span className="w-full px-0.5 text-center font-semibold whitespace-normal text-amber-700 dark:text-amber-300">
                    {t("products.possibleStock")}
                  </span>
                ) : isHomeDepotOnline ? (
                  <>
                    <div className={`w-[7px] h-[7px] rounded-full ${latestSalesFloor > 0 ? "bg-emerald-500" : "bg-red-500"}`} />
                    <span
                      className={`font-semibold truncate ${
                        latestSalesFloor > 0
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-gray-500 dark:text-dark-300"
                      }`}
                    >
                      {latestSalesFloor > 0 ? (samsClubStockLabel(retailer, latestSalesFloor, { inStock: t("products.inStock"), lowStock: t("products.lowStock") }) ?? t("products.inStockCount", { count: String(latestSalesFloor) })) : t("products.outOfStock")}
                    </span>
                  </>
                ) : isOnlineRetailer || isWoot || product.storeId === "online" ? (
                  <>
                    <div className="w-[7px] h-[7px] rounded-full bg-emerald-500" />
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400 truncate">
                      {t("products.availability")}
                    </span>
                  </>
                ) : isBestBuy || isCostco || (isLocalClearance && !hasInventoryCount) ? (
                  <>
                    <div className="w-[7px] h-[7px] rounded-full bg-emerald-500" />
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400 truncate">
                      {t("products.inStock")}
                    </span>
                  </>
                ) : (
                  <>
                    <div className={`w-[7px] h-[7px] rounded-full ${latestSalesFloor > 0 ? "bg-emerald-500" : "bg-red-500"}`} />
                    <span
                      className={`font-semibold truncate ${
                        latestSalesFloor > 0
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-gray-500 dark:text-dark-300"
                      }`}
                    >
                      {latestSalesFloor > 0 ? (samsClubStockLabel(retailer, latestSalesFloor, { inStock: t("products.inStock"), lowStock: t("products.lowStock") }) ?? t("products.inStockCount", { count: String(latestSalesFloor) })) : t("products.outOfStock")}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Right Column: Content */}
            <div className="flex-1 min-w-0 flex flex-col">
              {/* Row 1: Title & Save */}
              <div
                className="relative"
                style={{ paddingRight: (disableFavorites ? 0 : 34) + (onHideProduct && !isSavedMode ? 34 : 0) + (hasAffiliateLink ? 34 : 0) }}
              >
                <ProductDetailTrigger
                  sku={productDetailIdentifier}
                  storeId={isOnlineRetailer || isWoot ? "online" : product.storeId}
                  retailer={retailer === "walmart_online" ? "walmart" : retailer}
                  className="block w-full font-bold text-[13px] leading-[18px] tracking-[-0.1px] text-gray-900 dark:text-dark-50 text-left hover:text-primary-600 dark:hover:text-primary-400 transition-colors overflow-hidden"
                  style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                >
                  {product.name}{variantTitleSuffix}
                </ProductDetailTrigger>
                {(hasAffiliateLink || !disableFavorites || (onHideProduct && !isSavedMode)) && (
                  <div className="absolute -top-[3px] right-0 flex items-center">
                    {hasAffiliateLink && (
                      <button
                        type="button"
                        onClick={openCardShareSheet}
                        className="flex-shrink-0 rounded-md bg-transparent p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-primary-600 dark:text-dark-400 dark:hover:bg-dark-800 dark:hover:text-primary-400"
                        title={t("card.share")}
                        aria-label={t("card.share")}
                      >
                        <Share2 className="h-4 w-4" />
                      </button>
                    )}
                    {onHideProduct && !isSavedMode && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          onHideProduct(product);
                        }}
                        className="flex-shrink-0 rounded-md bg-transparent p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-dark-400 dark:hover:bg-dark-800 dark:hover:text-dark-100"
                        title={t("products.hideProduct")}
                        aria-label={t("products.hideProductNamed", { name: product.name })}
                      >
                        <EyeOff className="h-4 w-4" />
                      </button>
                    )}
                    {!disableFavorites && (isSavedMode ? (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onRemoveSaved?.(product.upc);
                        }}
                        className="!bg-transparent !p-1.5 hover:!bg-red-50 dark:hover:!bg-red-900/30 flex-shrink-0 text-red-500 hover:text-red-600 transition-colors rounded-md"
                        title={t("products.removeFromFavorites")}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    ) : (
                      <span {...(index === 0 ? { "data-tour": "product-save" } : {})}>
                        <SaveButton
                          productId={product.upc}
                          productName={product.name}
                          productImage={product.image}
                          productCategory={product.category}
                          productStoreId={product.storeId}
                          onlinePrice={product.onlinePrice}
                          storePrice={latestStorePrice}
                          stock={latestSalesFloor}
                          compact={true}
                          className="!bg-transparent !p-1.5 hover:!bg-gray-50 dark:hover:!bg-dark-800 flex-shrink-0"
                          walmartOnlineData={isOnlineRetailer ? {
                            msrp: product.msrp,
                            currentPrice: product.storePrice[product.storePrice.length - 1] || product.msrp,
                            currentPrices: product.storePrice,
                            url: product.url || "",
                            sku: product.sku,
                            lastUpdated: product.firstInserted,
                            lastUpdates: product.lastUpdates,
                            discountPercentage: product.discountPercentage,
                          } : undefined}
                        />
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {(storeLabel || (storeMatchCount > 1 && !isOnlineRetailer)) && (
                <div className="flex items-center gap-[5px] mt-0.5 min-w-0">
                  {storeLabel && (
                    <span className="text-[10px] font-bold text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 px-2 py-0.5 rounded-full truncate">
                      {storeLabel}
                    </span>
                  )}
                  {storeMatchCount > 1 && !isOnlineRetailer && (
                    <button onClick={handleProductClick} className="text-[10px] font-semibold text-purple-500 hover:text-purple-600 dark:text-purple-400 dark:hover:text-purple-300 underline underline-offset-2 whitespace-nowrap flex-shrink-0">
                      {t("products.moreStores", { count: String(storeMatchCount - 1) })}
                    </button>
                  )}
                </div>
              )}


              {/* Row 3: Price + Savings */}
              <div className="flex items-center flex-wrap gap-x-[5px] gap-y-0.5 mt-[3px]">
                {isPennyItem ? (
                  <span className="text-[17px] leading-none font-extrabold tracking-[-0.3px] tabular-nums text-red-600 dark:text-red-400">
                    $0.01
                  </span>
                ) : multiBuy ? (
                  <ProductOfferPrice display={multiBuy} />
                ) : (
                  <span className="text-[17px] leading-none font-extrabold tracking-[-0.3px] tabular-nums text-emerald-600 dark:text-emerald-400">
                    {formatPrice(latestStorePrice)}
                  </span>
                )}
                {isPotentialPennyItem && (
                  <span className="text-[9px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-200 bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded-md">
                    {t("products.potentialPennyBadge")}
                  </span>
                )}
                {!multiBuy && product.msrp > latestStorePrice && (
                  <span className="text-[11px] tabular-nums text-gray-400 dark:text-dark-400 line-through">
                    {formatPrice(product.msrp)}
                  </span>
                )}
                {isTarget && !product.discountedProduct ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowClearanceInfo(true); }}
                    className="flex items-center gap-0.5 text-[10px] font-semibold text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/40 px-1.5 py-0.5 rounded-md cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/60 transition-colors"
                  >
                    <span>{t("products.specialClearance")}</span>
                    <Info className="w-3 h-3" />
                  </button>
                ) : !multiBuy ? (
                  <>
                    {savingsAmount > 0 && (
                      <span className="text-[10px] font-bold tabular-nums text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded-md">
                        {t("products.saveAmount", { amount: formatPrice(savingsAmount) })}
                      </span>
                    )}
                    {discountPercent > 0 && (
                      <span className={`text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-md ${
                        discountPercent > 70
                          ? "text-orange-700 dark:text-orange-300 bg-orange-500/[0.12] dark:bg-orange-900/40"
                          : "text-red-600 dark:text-red-300 bg-red-500/10 dark:bg-red-900/40"
                      }`}>
                        {t("products.percentOffLabel", { percent: String(discountPercent) })}
                      </span>
                    )}
                    {cashbackAmount > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-bold tabular-nums text-teal-700 dark:text-teal-300 bg-teal-500/10 dark:bg-teal-900/40 px-1.5 py-0.5 rounded-md">
                        {cashbackLabel}
                      </span>
                    )}
                    <IbottaOfferChip offers={ibottaOffers} />
                    {offerLabel && (
                      <span className={`text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded-md ${
                        offerType === "bogo"
                          ? "text-indigo-700 dark:text-indigo-300 bg-indigo-500/10 dark:bg-indigo-900/40"
                          : "text-sky-700 dark:text-sky-300 bg-sky-500/10 dark:bg-sky-900/40"
                      }`}>
                        {offerLabel}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {cashbackAmount > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-bold tabular-nums text-teal-700 dark:text-teal-300 bg-teal-500/10 dark:bg-teal-900/40 px-1.5 py-0.5 rounded-md">
                        {cashbackLabel}
                      </span>
                    )}
                    <IbottaOfferChip offers={ibottaOffers} />
                  </>
                )}
              </div>

              {/* Sold by seller name for online products */}
              {(retailer === "walmart" || retailer === "walmart_online" || retailer === "amazon") && (isOnlineStoreId || isOnlineRetailer) && product.sellerName && (
                <div className="text-[10px] leading-3 text-gray-400 dark:text-dark-400 mt-0.5">
                  {t("products.soldBy")} <span className="font-medium text-gray-600 dark:text-dark-200">{isWalmart && product.sellerName?.toLowerCase() === "walmart.com" ? "Walmart" : product.sellerName}</span>
                </div>
              )}

              {/* SKU + Pickup Available / Open Box + Aisle */}
              {((!isBestBuy && !isCostco && !isWoot && (isValidUpc(product.upc) || isValidUpc(product.sku))) ||
                (retailer === "walmart" && product.pickupAvailable) ||
                hasVariantCriteria ||
                isBestBuyOpenBox ||
                aisleLabel) && (
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  {!isBestBuy && !isCostco && !isWoot && (
                    <span className="text-[11px] font-medium tabular-nums text-gray-400 dark:text-dark-400">
                      {isWalmart
                        ? (isValidUpc(product.upc) ? product.upc : "")
                        : isValidUpc(product.upc)
                          ? product.upc
                          : isValidUpc(product.sku)
                            ? product.sku
                            : ""}
                    </span>
                  )}
                  {aisleLabel && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-gray-600 dark:text-dark-200 bg-gray-100 dark:bg-dark-700 px-1.5 py-0.5 rounded-md max-w-[150px] truncate">
                      <MapPin className="w-2.5 h-2.5 flex-shrink-0 text-gray-400 dark:text-dark-400" />
                      {aisleLabel}
                    </span>
                  )}
                  {isBestBuyOpenBox && (
                    <span className="text-[10px] font-semibold tabular-nums text-amber-700 dark:text-amber-300 bg-amber-100/80 dark:bg-amber-900/40 px-1.5 py-0.5 rounded-md">
                      {getOpenBoxBadgeText(product, latestOpenBoxPrice, formatPrice, t)}
                    </span>
                  )}
                  {retailer === "walmart" && product.pickupAvailable && (
                    <span className="text-[10px] font-semibold text-sky-700 dark:text-sky-300 bg-sky-500/10 dark:bg-sky-900/40 px-1.5 py-0.5 rounded-md">
                      {t("products.pickupAvailable")}
                    </span>
                  )}
                  {hasVariantCriteria && (
                    <button
                      onClick={handleVariantCriteriaClick}
                      className="text-[10px] font-semibold text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/40 px-1.5 py-0.5 rounded-md hover:bg-purple-100 dark:hover:bg-purple-900/60 transition-colors cursor-pointer"
                      style={{ touchAction: "manipulation" }}
                      aria-expanded={isExpanded}
                    >
                      {t(variantCriteria.length === 1 ? "card.optionCount" : "card.optionsCount", { count: variantCriteria.length })}
                    </button>
                  )}
                </div>
              )}

              {/* Row 4: Time + Expand */}
              <div className="flex items-center justify-end gap-2 mt-auto pt-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`text-[10px] text-gray-400 dark:text-dark-400 ${
                      isPennyItem
                        ? "whitespace-normal text-right leading-3"
                        : "whitespace-nowrap truncate"
                    }`}
                  >
                    {isPennyItem ? (
                      <>
                        <span>{t("products.firstSpottedPenny")}: <span className="font-medium text-gray-500 dark:text-dark-300">{pennyFirstSpottedLabel}</span></span>
                        <span> · {t("products.lastSeenPenny")}: <span className="font-medium text-gray-500 dark:text-dark-300">{pennyLastRegularLabel}</span></span>
                      </>
                    ) : (
                      <>
                        {t("products.updatedLabel")}{" "}
                        <span className="font-medium text-gray-500 dark:text-dark-300">{lastUpdatedLabel}</span>
                      </>
                    )}
                  </span>
                  <button
                    onPointerDown={handleProductClick}
                    {...(index === 0 ? { "data-tour": "product-expand" } : {})}
                    className={`w-7 h-7 flex items-center justify-center rounded-full border transition-[background-color,border-color,color,box-shadow,transform] duration-200 ease-out cursor-pointer active:scale-95 ${
                      isExpanded
                        ? "bg-primary-600 border-primary-600 text-white shadow-md scale-105"
                        : "bg-white dark:bg-dark-800 border-gray-200 dark:border-dark-600 text-gray-500 dark:text-dark-300 hover:border-gray-300 dark:hover:border-dark-400 hover:text-gray-700 dark:hover:text-dark-100 hover:bg-gray-50 dark:hover:bg-dark-700 active:scale-95"
                    }`}
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? t("card.collapseDetails") : t("card.expandDetails")}
                  >
                    <Plus className={`w-4 h-4 transform-gpu transition-transform duration-[260ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${isExpanded ? "rotate-45" : ""}`} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Expanded Content */}
          {showExpandedContent && (
            <div
              className="grid transition-[grid-template-rows,opacity] duration-[260ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
              style={{
                gridTemplateRows: isExpanded ? "1fr" : "0fr",
                opacity: isExpanded ? 1 : 0,
              }}
              aria-hidden={!isExpanded}
            >
              <div className="overflow-hidden">
            <div className={`mt-3 pt-3 border-t border-gray-100 dark:border-dark-700 space-y-3 origin-top transform-gpu transition-[opacity,transform] duration-[220ms] ease-out motion-reduce:transition-none ${
              isExpanded ? "translate-y-0 scale-y-100 opacity-100" : "-translate-y-2 scale-y-[0.98] opacity-0 pointer-events-none"
            }`}>
              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {!isWoot && !isOnlineStoreId && !isLowes && (
                  <button
                    onClick={handleViewStores}
                    className="flex-1 min-w-0 whitespace-nowrap flex items-center justify-center gap-1.5 h-9 px-2.5 rounded-xl text-xs font-semibold bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 active:scale-[0.98] transition-all cursor-pointer"
                    style={{ touchAction: "manipulation" }}
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    {t("products.nearbyButton")}
                  </button>
                )}

                {!isTarget && !isOnlineRetailer && !isBestBuy && !isCostco && !isLowes && !isWoot && !isOnlineStoreId && (
                  <SetAlertButton
                    productUpc={product.upc}
                    productSku={getRetailerLookupSku(product)}
                    productName={product.name}
                    productImage={product.image}
                    productCategory={product.category}
                    msrp={product.msrp}
                    currentPrice={latestStorePrice}
                    className="flex-1 min-w-0 whitespace-nowrap"
                    icon="bell"
                  />
                )}

                {hasAffiliateLink ? (
                  <button
                    onClick={handleAffiliateClick}
                    disabled={affiliateLoading}
                    className="flex-1 min-w-0 whitespace-nowrap flex items-center justify-center gap-1.5 h-9 px-2.5 rounded-xl text-xs font-semibold bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-dark-200 hover:bg-gray-200 dark:hover:bg-dark-600 active:scale-[0.98] transition-all cursor-pointer disabled:opacity-60"
                    style={{ touchAction: "manipulation" }}
                  >
                    {affiliateLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    {getRetailerLinkLabel(retailer)}
                  </button>
                ) : (
                  <a
                    href={productUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 min-w-0 whitespace-nowrap flex items-center justify-center gap-1.5 h-9 px-2.5 rounded-xl text-xs font-semibold bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-dark-200 hover:bg-gray-200 dark:hover:bg-dark-600 active:scale-[0.98] transition-all cursor-pointer"
                    style={{ touchAction: "manipulation" }}
                  >
                    {getRetailerLinkLabel(retailer)}
                  </a>
                )}

                <button
                  onClick={handleShareDeal}
                  disabled={shareLoading}
                  className="flex items-center justify-center h-9 w-9 flex-shrink-0 rounded-xl text-gray-400 dark:text-dark-400 hover:text-primary-600 dark:hover:text-primary-400 bg-gray-50 dark:bg-dark-800 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors disabled:opacity-50"
                  style={{ touchAction: "manipulation" }}
                  aria-label={t("card.share")}
                >
                  {shareLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                </button>
              </div>

              {hasVariantCriteria && retailer === "walmart" && (
                <div className="rounded-xl border border-purple-100 dark:border-purple-900/40 bg-gradient-to-br from-purple-50/80 to-white dark:from-purple-950/30 dark:to-dark-800 px-3 py-2.5">
                  <div className="mb-2 flex items-center gap-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-purple-700 dark:text-purple-300">
                      {t("products.productOptions")}
                    </span>
                    <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-purple-200/70 dark:bg-purple-900/60 px-1 text-[9px] font-bold text-purple-700 dark:text-purple-200">
                      {variantCriteria.length}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-2.5">
                    {variantCriteria.map((option, optionIndex) => {
                      const label = option.displayName || option.name || option.sku;
                      const isUnavailable = option.availabilityStatus && option.availabilityStatus !== "AVAILABLE";

                      return (
                        <button
                          key={`${option.sku}-${optionIndex}`}
                          type="button"
                          onClick={(e) => handleVariantOptionClick(e, option)}
                          disabled={!option.sku}
                          aria-label={`${t("products.nearbyButton")} ${label}`}
                          title={`${label}${isUnavailable ? ` · ${t("card.unavailable")}` : ""}`}
                          className={`group flex w-[58px] flex-col items-center text-center transition-transform duration-150 ease-out ${
                            isUnavailable ? "opacity-60" : "hover:-translate-y-0.5"
                          } ${option.sku ? "cursor-pointer" : "cursor-default"}`}
                          style={{ touchAction: "manipulation" }}
                        >
                          <div className="relative h-12 w-12 overflow-hidden rounded-full bg-white ring-1 ring-purple-200/80 shadow-sm transition-all duration-150 ease-out dark:bg-dark-800 dark:ring-purple-800/60 group-hover:ring-2 group-hover:ring-purple-500 group-hover:shadow-md group-active:scale-95">
                            <div className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-purple-400 dark:text-purple-300">
                              {label.slice(0, 1).toUpperCase()}
                            </div>
                            {option.image && (
                              <img
                                src={getProductImageUrl(option.image, "80")}
                                alt={label}
                                className="relative h-full w-full object-cover"
                                loading="lazy"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            )}
                            {!isUnavailable && (
                              <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-dark-800" />
                            )}
                          </div>
                          <div className="mt-1 w-full truncate text-[10px] font-medium leading-tight text-gray-700 dark:text-dark-200 group-hover:text-purple-700 dark:group-hover:text-purple-300">
                            {label}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2 flex items-center gap-1 text-[10px] italic text-purple-500/80 dark:text-purple-400/80">
                    <MapPin className="h-2.5 w-2.5 flex-shrink-0" />
                    <span className="truncate">{t("products.optionsTapHint")}</span>
                  </div>
                </div>
              )}

              {/* Multi-Store Availability List */}
              {storeMatchCount > 1 && (
                <div className="border border-gray-100 dark:border-dark-700 rounded-xl overflow-hidden bg-white dark:bg-dark-800">
                  <div className="bg-gray-50/80 dark:bg-dark-900/50 px-3.5 py-2.5 border-b border-gray-100 dark:border-dark-700">
                    <h4 className="text-[10px] font-bold text-gray-400 dark:text-dark-400 uppercase tracking-wider">
                      {t("products.nearbyCount", { count: String(storeMatchCount) })}
                    </h4>
                  </div>
                  <div className="divide-y divide-gray-100 dark:divide-dark-700">
                    {storeMatchesQuery.isLoading && (
                      <div className="flex items-center gap-2 px-3.5 py-3 text-xs text-gray-500 dark:text-dark-400">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Loading store matches…
                      </div>
                    )}
                    {[...allProductsWithSameUPC]
                      .sort((a, b) => {
                        const priceA =
                          a.storePrice[a.storePrice.length - 1] || 0;
                        const priceB =
                          b.storePrice[b.storePrice.length - 1] || 0;
                        return priceA - priceB;
                      })
                      .map((storeProduct, idx) => {
                        const currentPrice =
                          storeProduct.storePrice[
                            storeProduct.storePrice.length - 1
                          ] || 0;
                        const inventory =
                          storeProduct.salesFloor[
                            storeProduct.salesFloor.length - 1
                          ] || 0;
                        const hasStoreInventoryCount =
                          Array.isArray(storeProduct.salesFloor) && storeProduct.salesFloor.length > 0;
                        const storeIdFormatted = formatStoreId(
                          storeProduct.storeId
                        );
                        const storeName =
                          storeFilterOptions?.find(
                            (option) => option.id === storeIdFormatted
                          )?.name || (isCostco ? t("card.warehouseNumber", { id: storeIdFormatted.replace(/-wh$/, "") }) : t("products.storeNumber", { id: storeIdFormatted }));

                        const displayStoreName = (() => {
                          if (isCostco) return storeName;
                          const parts = storeName.split(",");
                          if (parts.length >= 2) return parts[1].trim();
                          return storeName.split(" ").slice(0, 3).join(" ");
                        })();

                        const isBestPrice = idx === 0;

                        return (
                          <div
                            key={`${storeProduct.storeId}-${storeProduct.upc}`}
                            className="flex items-center justify-between px-3.5 py-2.5 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                          >
                            <div className="flex-1 min-w-0 pr-3">
                              <div className="flex items-center gap-2 mb-0.5">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      let fullAddress =
                                        displayStoreName || storeName;
                                      if (storeName && !storeName.includes(",")) {
                                        fullAddress = `${storeName} ${t("products.storeNumber", { id: storeIdFormatted })}`;
                                      }
                                      handleOpenMapModal(
                                        fullAddress,
                                        t("products.storeNumber", { id: storeIdFormatted }),
                                        storeIdFormatted
                                      );
                                    }}
                                    className="text-[13px] font-semibold text-purple-600 hover:text-purple-500 dark:text-purple-400 dark:hover:text-purple-300 underline underline-offset-2 decoration-purple-300/60 dark:decoration-purple-500/40 truncate text-left"
                                  >
                                  {displayStoreName}
                                </button>
                                {isBestPrice && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                    {t("products.bestLabel")}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-dark-400">
                                <span className="tabular-nums">
                                  #{storeIdFormatted}
                                </span>
                                <span className="w-[3px] h-[3px] rounded-full bg-gray-300 dark:bg-dark-600"></span>
                                <span
                                  className={`font-medium ${
                                    storeProduct.storeId.includes("-PENNY-ITEM")
                                      ? "text-amber-700 dark:text-amber-300"
                                      : isBestBuy || isCostco || (isLocalClearance && !hasStoreInventoryCount) || inventory > 0
                                      ? "text-emerald-600 dark:text-emerald-400"
                                      : "text-red-500 dark:text-red-400"
                                  }`}
                                >
                                {storeProduct.storeId.includes("-PENNY-ITEM")
                                    ? t("products.possibleStock")
                                    : isBestBuy || isCostco || (isLocalClearance && !hasStoreInventoryCount)
                                    ? t("products.inStock")
                                    : inventory > 0
                                    ? samsClubStockLabel(retailer, inventory, { inStock: t("products.inStock"), lowStock: t("products.lowStock") }) ?? t("products.inStockCount", { count: String(inventory) })
                                    : t("products.outLabel")}
                                </span>
                                {storeProduct.aisle && !isLowes && (
                                  <>
                                    <span className="w-[3px] h-[3px] rounded-full bg-gray-300 dark:bg-dark-600"></span>
                                    <span className="font-medium text-gray-600 dark:text-dark-200">
                                      {storeProduct.aisle}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>

                            <div className="text-right">
                              {storeProduct.storeId.includes("-PENNY-ITEM") ? (
                                <div className="flex flex-col items-end">
                                  <span className="text-[15px] font-bold tabular-nums text-red-600 dark:text-red-400">
                                    $0.01
                                  </span>
                                  {isDollarGeneral && (
                                    <span className="text-[8px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                                      {t("products.potentialPennyBadge")}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <div
                                  className={`text-[15px] font-bold tabular-nums ${
                                    isBestPrice
                                      ? "text-emerald-600 dark:text-emerald-400"
                                      : "text-gray-900 dark:text-white"
                                  }`}
                                >
                                  {formatPrice(currentPrice)}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Deal Insights Detail */}
              {product.insights && product.insights.score > 0 && (
                <div className="rounded-xl p-3.5 bg-gray-50/80 dark:bg-dark-800 border border-gray-100 dark:border-dark-700">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`text-xs font-bold ${
                      product.insights.verdict === "BUY_NOW"
                        ? "text-emerald-600 dark:text-emerald-400"
                        : product.insights.verdict === "GOOD_DEAL"
                        ? "text-emerald-600 dark:text-emerald-400"
                        : product.insights.verdict === "WAIT"
                        ? "text-yellow-600 dark:text-yellow-400"
                        : "text-gray-500 dark:text-dark-400"
                    }`}>
                      {product.insights.verdict === "BUY_NOW" ? t("card.verdictBuyNow") :
                       product.insights.verdict === "GOOD_DEAL" ? t("card.verdictGoodDeal") :
                       product.insights.verdict === "WAIT" ? t("card.verdictWait") : t("card.verdictOverpriced")}
                    </span>
                    <span className="text-[10px] text-gray-400 dark:text-dark-500">•</span>
                    <span className="text-[10px] text-gray-500 dark:text-dark-400">
                      {t("card.score")} <span className="font-semibold text-gray-700 dark:text-dark-200">{product.insights.score}</span>/100
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-600 dark:text-dark-300 leading-relaxed">
                    {product.insights.reasoning}
                  </p>
                  <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-100 dark:border-dark-700 text-[10px] text-gray-500 dark:text-dark-400">
                    <span>{t("card.low")} <span className="font-semibold text-gray-700 dark:text-dark-200">{formatPrice(product.insights.historicalLow)}</span></span>
                    <span>{t("card.high")} <span className="font-semibold text-gray-700 dark:text-dark-200">{formatPrice(product.insights.historicalHigh)}</span></span>
                    <span>{t("card.daysTracked", { count: product.insights.daysTracked })}</span>
                  </div>
                </div>
              )}

              {/* Barcode & Details */}
              <div className="bg-gray-50/80 dark:bg-dark-800 border border-gray-100 dark:border-dark-700 rounded-xl p-3.5">
                {/* Category and View Options */}
                {adjustedCategory(product) && (
                  <div className="mb-3 pb-3 border-b border-gray-200/70 dark:border-dark-700">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-dark-400 block mb-0.5">
                          {t("common.category")}
                        </span>
                        <span className="text-xs font-medium text-gray-700 dark:text-dark-200">
                          {categoryLabel(adjustedCategory(product)) || categoryName}
                        </span>
                      </div>
                      {/* View Options Button - compact version */}
                      {product.variants && product.variants.length > 0 && (
                        <button
                          onPointerDown={handleViewOptionsClick}
                          className="flex-shrink-0 flex items-center px-2.5 py-1.5 text-xs font-semibold bg-purple-50 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/60 rounded-lg transition-colors cursor-pointer"
                          style={{ touchAction: "manipulation" }}
                        >
                          <span>{t("products.optionsButton")}</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Timeline */}
                <div className={`grid ${isPennyItem ? "grid-cols-2" : "grid-cols-3"} gap-2 mb-3 pb-3 border-b border-gray-200/70 dark:border-dark-700`}>
                  {isPennyItem ? (
                    <>
                      <div className="min-w-0">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-dark-400 block mb-0.5">{t("products.firstSpottedPenny")}</span>
                        <span className="text-xs font-medium text-gray-700 dark:text-dark-200">{pennyFirstSpottedLabel}</span>
                      </div>
                      <div className="min-w-0">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-dark-400 block mb-0.5">{t("products.lastSeenPenny")}</span>
                        <span className="text-xs font-medium text-gray-700 dark:text-dark-200">{pennyLastRegularLabel}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="min-w-0">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-dark-400 block mb-0.5 truncate">{t("products.firstUpdated")}</span>
                        <span className="text-xs font-medium text-gray-700 dark:text-dark-200">{formatFirstAppeared(product.firstInserted, { t })}</span>
                      </div>
                      <div className="min-w-0">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-dark-400 block mb-0.5 truncate">{t("products.lastChanged")}</span>
                        <span className="text-xs font-medium text-gray-700 dark:text-dark-200">{lastChangeLabel}</span>
                      </div>
                      <div className="min-w-0">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-dark-400 block mb-0.5 truncate">{t("products.lastUpdatedLabel")}</span>
                        <span className="text-xs font-medium text-gray-700 dark:text-dark-200">{lastUpdatedLabel}</span>
                      </div>
                    </>
                  )}
                </div>
                {(isValidUpc(product.sku) || isValidUpc(product.upc)) && !isWoot && (
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-dark-400 block mb-1">
                      {t("products.productCode")}
                    </span>
                    <div className="flex flex-wrap gap-x-3 gap-y-2 text-xs font-medium tabular-nums text-gray-700 dark:text-dark-100">
                      {isWalmart && isValidUpc(product.upc) ? (
                        <div className="flex items-center gap-1">
                          <span>{t("card.sku")}: {product.upc}</span>
                          <button
                            onPointerDown={(e) => handleCopyToClipboard(e, product.upc, t("card.sku"))}
                            className="p-0.5 text-gray-400 dark:text-dark-500 hover:text-gray-600 dark:hover:text-dark-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded transition-colors cursor-pointer"
                            title={t("card.copyCode", { code: t("card.sku") })}
                            style={{ touchAction: "manipulation" }}
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        isValidUpc(product.upc) && (
                          <div className="flex items-center gap-1">
                            <span>{isHomeDepot ? t("card.itemId") : isTarget ? t("card.tcin") : t("card.sku")}: {product.upc}</span>
                            <button
                              onPointerDown={(e) => handleCopyToClipboard(e, product.upc, isHomeDepot ? t("card.itemId") : isTarget ? t("card.tcin") : t("card.sku"))}
                              className="p-0.5 text-gray-400 dark:text-dark-500 hover:text-gray-600 dark:hover:text-dark-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded transition-colors cursor-pointer"
                              title={t("card.copyCode", { code: isHomeDepot ? t("card.itemId") : isTarget ? t("card.tcin") : t("card.sku") })}
                              style={{ touchAction: "manipulation" }}
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                            {isTarget && (
                              <button
                                onPointerDown={(e) => handleBarcodeClick(e, product.upc)}
                                className="text-purple-600 dark:text-purple-400 underline font-medium hover:text-purple-700 dark:hover:text-purple-300 cursor-pointer"
                                style={{ touchAction: "manipulation" }}
                              >
                                {t("products.viewBarcode")}
                              </button>
                            )}
                          </div>
                        )
                      )}
                      {isWalmart && (
                        <WalmartBarcodeActions
                          barcode={getBarcodeFromProduct() ?? walmartCodes?.barcode}
                          onBarcodeClick={(e, barcode) => handleBarcodeClick(e, barcode)}
                          onCopyToClipboard={handleCopyToClipboard}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>

            </div>
            </div>
            </div>
          )}
        </div>
      </div>

      {/* Render modals via portal */}
      {imageModal && createPortal(imageModal, document.body)}
      {variantsModal && createPortal(variantsModal, document.body)}

      {/* Home Depot zip code input modal */}
      {showHomeDepotZipInput &&
        createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(4px)",
            }}
            onPointerDown={() => setShowHomeDepotZipInput(false)}
          >
            <div
              className="bg-white dark:bg-dark-800 rounded-xl p-6 max-w-sm w-full"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold dark:text-white mb-4">{t("products.enterZipCodeTitle")}</h3>
              <p className="text-sm text-gray-600 dark:text-dark-300 mb-4">
                {t("products.enterZipCodeDescription")}
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
                    {t("products.zipCodeLabel")}
                  </label>
                  <input
                    type="text"
                    value={homeDepotZipCode}
                    onChange={(e) => setHomeDepotZipCode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 dark:bg-dark-700 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t("card.zipPlaceholder")}
                    maxLength={10}
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    onPointerDown={handleHomeDepotZipSubmit}
                    className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-700 cursor-pointer"
                    style={{ touchAction: "manipulation" }}
                  >
                    {t("products.searchStoresButton")}
                  </button>
                  <button
                    onPointerDown={() => setShowHomeDepotZipInput(false)}
                    className="flex-1 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-dark-200 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-dark-600 cursor-pointer"
                    style={{ touchAction: "manipulation" }}
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Multi-store modal */}
      {showMultiStoreModal &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-dark-800 rounded-lg max-w-md w-full max-h-96 overflow-hidden">
              <div className="p-4 border-b border-gray-200 dark:border-dark-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {product.name} - {t("products.locationAvailabilityTitle")}
                </h3>
                <button
                  onPointerDown={() => setShowMultiStoreModal(false)}
                  className="text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-200 transition-colors cursor-pointer"
                  style={{ touchAction: "manipulation" }}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-80">
                <div className="space-y-3">
                  {allProductsWithSameUPC
                    .sort((a, b) => {
                      const priceA = a.storePrice[a.storePrice.length - 1] || 0;
                      const priceB = b.storePrice[b.storePrice.length - 1] || 0;
                      return priceA - priceB; // Sort by price, lowest first
                    })
                    .map((storeProduct, idx) => {
                      const storePrice =
                        storeProduct.storePrice[
                          storeProduct.storePrice.length - 1
                        ] || 0;
                      const storeName =
                        storeFilterOptions?.find(
                          (option) => option.id === storeProduct.storeId
                        )?.name || (isCostco ? t("card.warehouseNumber", { id: storeProduct.storeId.replace(/-wh$/, "") }) : t("card.storeId", { id: storeProduct.storeId }));
                      const isLowestPrice = idx === 0;

                      return (
                        <div
                          key={`${storeProduct.storeId}-${storeProduct.upc}`}
                          className={`p-3 rounded-lg border ${
                            isLowestPrice
                              ? "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30"
                              : "border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-800"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                {storeName}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center space-x-2">
                                {storeProduct.storeId.includes(
                                  "-PENNY-ITEM"
                                ) ? (
                                  <>
                                    <span className="text-lg font-bold text-red-600 dark:text-red-400">
                                      $0.01
                                    </span>
                                    <span className="text-lg font-bold text-gray-500 dark:text-dark-400 line-through">
                                      ${storePrice.toFixed(2)}
                                    </span>
                                  </>
                                ) : (
                                  <div
                                    className={`text-lg font-bold ${
                                      isLowestPrice
                                        ? "text-green-600 dark:text-green-400"
                                        : "text-gray-900 dark:text-white"
                                    }`}
                                  >
                                    ${storePrice.toFixed(2)}
                                  </div>
                                )}
                              </div>
                              {isLowestPrice && (
                                <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                                  {t("products.lowestPriceLabel")}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Store Map Modal */}
      <StoreMapModal
        isOpen={showMapModal}
        onClose={() => setShowMapModal(false)}
        storeAddress={selectedStoreForMap?.address || ""}
        storeName={selectedStoreForMap?.name}
        coordinates={selectedStoreForMap?.coordinates}
      />

      {/* Target Clearance Info Popup */}
      {hasAffiliateLink && (
        <ShareDealSheet
          isOpen={shareSheetOpen}
          onClose={() => setShareSheetOpen(false)}
          title={product.name}
          text={`${product.name} for ${formatPrice(latestStorePrice)}${discountPercent > 0 ? ` (${discountPercent}% off)` : ""} at ${getRetailerDisplayName(retailer as RetailerType)}`}
          priceLine={`${formatPrice(latestStorePrice)}${discountPercent > 0 ? ` (${discountPercent}% off)` : ""} at ${getRetailerDisplayName(retailer as RetailerType)}`}
          imageUrl={product.image ? getProductImageUrl(product.image) : undefined}
          resolveUrl={resolveCardShareUrl}
        />
      )}
      {showClearanceInfo && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", backdropFilter: "blur(4px)" }}
          onPointerDown={() => setShowClearanceInfo(false)}
        >
          <div
            className="bg-white dark:bg-dark-800 rounded-xl p-5 max-w-sm w-full shadow-xl"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/40 rounded-full flex items-center justify-center">
                <Info className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t("products.clearanceInfoTitle")}</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-dark-300 leading-relaxed">
              {savingsAmount > 0
                ? t("products.clearanceInfoWithSavings")
                : t("products.clearanceInfoWithoutSavings")}
            </p>
            <button
              onPointerDown={() => setShowClearanceInfo(false)}
              className="mt-4 w-full py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors cursor-pointer"
              style={{ touchAction: "manipulation" }}
            >
              {t("products.gotIt")}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Barcode Bottom Sheet */}
      {showBarcodeSheet && createPortal(
        <div
          className="fixed inset-0 z-[10000] flex items-end justify-center"
          style={{
            backgroundColor: isBarcodeSheetAnimating ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0)",
            backdropFilter: isBarcodeSheetAnimating ? "blur(4px)" : "blur(0px)",
            transition: "background-color 250ms ease-out, backdrop-filter 250ms ease-out",
          }}
          onPointerDown={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseBarcodeSheet();
            }
          }}
        >
          <div
            className={`bg-white dark:bg-dark-900 w-full max-w-lg rounded-t-2xl overflow-hidden transform transition-all duration-300 ease-out ${
              isBarcodeSheetAnimating ? "translate-y-0" : "translate-y-full"
            }`}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 dark:bg-dark-600 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-3 border-b border-gray-100 dark:border-dark-700">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gray-100 dark:bg-dark-700 rounded-lg flex items-center justify-center">
                  <Barcode className="w-4 h-4 text-gray-600 dark:text-dark-300" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t("products.productBarcode")}</h3>
              </div>
              <button
                onPointerDown={handleCloseBarcodeSheet}
                className="p-2 text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-200 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors cursor-pointer"
                style={{ touchAction: "manipulation" }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {isFetchingBarcode ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="w-16 h-16 mb-4 bg-gray-100 dark:bg-dark-700 rounded-2xl flex items-center justify-center animate-pulse">
                    <Barcode className="w-8 h-8 text-gray-400 dark:text-dark-500" />
                  </div>
                  <p className="text-sm font-medium text-gray-600 dark:text-dark-300 mb-1">{t("products.gettingBarcode")}</p>
                  <p className="text-xs text-gray-400 dark:text-dark-500">{t("products.gettingBarcodeWait")}</p>
                </div>
              ) : fetchedBarcode ? (
                <div className="flex flex-col items-center">
                  {/* Product name */}
                  <p className="text-sm text-gray-600 dark:text-dark-300 text-center mb-4 line-clamp-2">
                    {product.name}
                  </p>

                  {/* Barcode */}
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <BarcodeComponent
                      upc={String(fetchedBarcode)}
                      width={2}
                      height={80}
                      fontSize={14}
                      displayValue={true}
                      clickable={false}
                      forceCode128={isTarget}
                    />
                  </div>

                  {/* UPC text */}
                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-sm text-gray-500 dark:text-dark-400">
                      {isTarget ? t("card.tcin") : t("card.upc")}: {fetchedBarcode}
                    </span>
                    <button
                      onPointerDown={(e) => handleCopyToClipboard(e, fetchedBarcode, isTarget ? t("card.tcin") : t("card.upc"))}
                      className="p-1.5 text-gray-400 dark:text-dark-500 hover:text-gray-600 dark:hover:text-dark-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-md transition-colors cursor-pointer"
                      title={t("card.copyCode", { code: isTarget ? t("card.tcin") : t("card.upc") })}
                      style={{ touchAction: "manipulation" }}
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Hint */}
                  <p className="mt-4 text-xs text-gray-400 dark:text-dark-500 text-center">
                    {t("products.scanBarcodeHint")}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="w-16 h-16 mb-4 bg-red-50 dark:bg-red-900/20 rounded-2xl flex items-center justify-center">
                    <Barcode className="w-8 h-8 text-red-400 dark:text-red-500" />
                  </div>
                  <p className="text-sm font-medium text-gray-600 dark:text-dark-300 mb-1">{t("products.barcodeNotAvailable")}</p>
                  <p className="text-xs text-gray-400 dark:text-dark-500 text-center">
                    {t("products.barcodeNotAvailableDesc")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// Custom comparison to prevent unnecessary re-renders
export default memo(ProductItem, areProductItemPropsEqual);
